import { useEffect, useMemo, useState } from "react";

const RANGES = [
  { k: "15m", l: "15M" },
  { k: "1h", l: "1H" },
  { k: "4h", l: "4H" },
  { k: "1d", l: "1D" },
  { k: "1w", l: "1W" },
  { k: "1mo", l: "1M" },
  { k: "1y", l: "1Y" },
];

export { RANGES };

export const chartFmt = (n, dp = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

// deterministic random walk driver for the "live tick" overlay on the last candle
function nextTick(seed) {
  const x = Math.sin(seed) * 10000;
  return (x - Math.floor(x)) * 2 - 1; // [-1, 1]
}

export default function CandleChart({ candles, mode, prefix = "$", live = true }) {
  const [tickN, setTickN] = useState(0);

  // reset tick counter whenever a fresh candles array arrives
  useEffect(() => {
    setTickN(0);
  }, [candles]);

  // drive the live tick animation (~4 Hz) — pauses when tab is hidden
  useEffect(() => {
    if (!live || !candles || candles.length < 2) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") setTickN((n) => n + 1);
    }, 250);
    return () => clearInterval(id);
  }, [live, candles]);

  // overlay a jittered last candle so the chart never looks frozen between refetches
  const shownCandles = useMemo(() => {
    if (!candles || candles.length < 2) return candles;
    if (!live || tickN === 0) return candles;
    const last = candles[candles.length - 1];
    const prev = candles[candles.length - 2];
    const [t, o, h, l, c, v] = last;
    // volatility scale from recent range; slightly punchier so movement is visible
    const vol = Math.max(Math.abs(h - l), Math.abs(c - prev[4]), Math.abs(c) * 0.0008) * 0.75;
    const drift = nextTick(t + tickN * 3.7) * vol;
    const newC = Math.max(0, c + drift);
    const newH = Math.max(h, newC);
    const newL = Math.min(l, newC);
    const out = candles.slice(0, -1);
    out.push([t, o, newH, newL, newC, v]);
    return out;
  }, [candles, live, tickN]);

  if (!shownCandles || shownCandles.length < 2) {
    return <div className="flex h-52 items-center justify-center text-xs text-white/25">Chart unavailable</div>;
  }
  const w = 600;
  const h = 190;
  const volH = 36;
  const closes = shownCandles.map((c) => c[4]);
  const min = Math.min(...shownCandles.map((c) => c[3]));
  const max = Math.max(...shownCandles.map((c) => c[2]));
  const range = max - min || 1;
  const y = (v) => h - ((v - min) / range) * (h - 16) - 8;
  const n = shownCandles.length;
  const cw = w / n;
  const vols = shownCandles.map((c) => c[5] || 0);
  const maxVol = Math.max(...vols) || 1;
  const hasVol = vols.some((v) => v > 0);
  const up = closes[closes.length - 1] >= closes[0];
  const lineColor = up ? "#26A69A" : "#EF5350";

  let line = null;
  if (mode === "line") {
    const d = closes
      .map((v, i) => `${i ? "L" : "M"}${(i * cw + cw / 2).toFixed(1)} ${y(v).toFixed(1)}`)
      .join(" ");
    line = (
      <>
        <path d={`${d} L${w} ${h} L0 ${h} Z`} fill={lineColor} opacity="0.08" />
        <path d={d} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinejoin="round" />
      </>
    );
  }

  const lastX = (n - 1) * cw + cw / 2;
  const lastY = y(closes[closes.length - 1]);

  return (
    <div data-testid="candle-chart">
      <svg viewBox={`0 0 ${w} ${h + (hasVol && mode === "candles" ? volH + 10 : 0)}`} className="h-52 w-full sm:h-64">
        {mode === "line"
          ? line
          : shownCandles.map((c, i) => {
              const x = i * cw + cw / 2;
              const bull = c[4] >= c[1];
              const color = bull ? "#26A69A" : "#EF5350";
              const bodyTop = y(Math.max(c[1], c[4]));
              const bodyH = Math.max(1, y(Math.min(c[1], c[4])) - bodyTop);
              return (
                <g key={i}>
                  <line x1={x} y1={y(c[2])} x2={x} y2={y(c[3])} stroke={color} strokeWidth={Math.max(0.6, cw * 0.12)} />
                  <rect x={i * cw + cw * 0.18} y={bodyTop} width={Math.max(0.9, cw * 0.64)} height={bodyH} fill={color} />
                  {hasVol && (
                    <rect
                      x={i * cw + cw * 0.18}
                      y={h + 10 + volH - ((c[5] || 0) / maxVol) * volH}
                      width={Math.max(0.9, cw * 0.64)}
                      height={((c[5] || 0) / maxVol) * volH}
                      fill={color}
                      opacity="0.45"
                    />
                  )}
                </g>
              );
            })}
        {live && (
          <g>
            <circle cx={lastX} cy={lastY} r="2.5" fill={lineColor}>
              <animate attributeName="r" values="2.5;5;2.5" dur="1.6s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="1;0.4;1" dur="1.6s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
      </svg>
      <div className="flex justify-between text-[10px] text-white/30">
        <span className="font-tabular">{prefix}{chartFmt(min)}</span>
        <span className="font-tabular">{prefix}{chartFmt(max)}</span>
      </div>
    </div>
  );
}
